import React, { useState, useEffect, useMemo } from "react";
import { useDirtyFlag } from "@/hooks/useUnsavedChanges";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Command, CommandInput } from "@/components/ui/command";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { DismissibleChip } from "@/components/consultation/shared/DismissibleChip";
import { Table, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { Loader2, AlertTriangle, Search, Undo2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/consultation/EmptyState";
import { useNutritionEvaluationContext } from "../NutritionEvaluationContext";
import {
  useHealthProfile,
  useUpdateHealthProfile,
  useSleepData,
  useUpdateSleepData,
  useFoodFrequency,
  useUpdateFoodFrequency,
  useDietaryPreference,
  useUpdateDietaryPreference,
  useSetFeedingType,
  useLikedFood,
  useDislikedFood,
  useAddFoodPreference,
  useRemoveFoodPreference,
  useAppointmentNutritionSymptoms,
  useAddNutritionSymptom,
  useRemoveNutritionSymptom,
  useSearchNutritionSymptoms,
  useFoodSearch,
} from "@/hooks/useNutritionEvaluation";
import type {
  SleepData,
  FoodFrequency,
  ActivityLevel,
  NutritionSymptom,
  AppointmentNutritionSymptom,
} from "@/types/nutrition";
import {
  ACTIVITY_LEVEL_OPTIONS,
  REGIONAL_PREFERENCE_OPTIONS,
} from "@/types/nutrition";

// =============================================================================
// Measurement Field (with unit switcher)
// =============================================================================

const numericGuard = (v: string) => v === "" || /^\d*\.?\d*$/.test(v);

const fmt = (n: number, dp = 1) => {
  const r = parseFloat(n.toFixed(dp));
  return Number.isFinite(r) ? r.toString() : "";
};

interface UnitSwitcherProps {
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

const UnitSwitcher: React.FC<UnitSwitcherProps> = ({
  options,
  value,
  onChange,
  disabled,
}) => (
  <div className="inline-flex items-center rounded-full bg-muted p-0.5">
    {options.map((opt) => (
      <button
        type="button"
        key={opt}
        tabIndex={-1}
        onClick={() => onChange(opt)}
        disabled={disabled || value === opt}
        className={cn(
          "z-hint rounded-full px-1.5 py-0.5 transition-colors disabled:opacity-100",
          value === opt
            ? "bg-card text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        {opt}
      </button>
    ))}
  </div>
);

interface MeasurementFieldProps {
  label: string;
  className?: string;
  disabled?: boolean;
  isDirty?: boolean;
  originalLabel?: string;
  onRevert?: () => void;
  unit: string;
  unitOptions?: readonly string[];
  onUnitChange?: (u: string) => void;
  value: string;
  onChange: (value: string) => void;
  // Compound mode (e.g. height ft+in): second input + unit shown inside the same pill
  compound?: boolean;
  value2?: string;
  onChange2?: (value: string) => void;
  unit2?: string;
}

const MeasurementField: React.FC<MeasurementFieldProps> = ({
  label,
  className,
  disabled = false,
  isDirty = false,
  originalLabel,
  onRevert,
  unit,
  unitOptions,
  onUnitChange,
  value,
  onChange,
  compound = false,
  value2,
  onChange2,
  unit2,
}) => {
  const baseInput =
    "z-body z-numeric h-full w-full min-w-0 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-2">
        <Label className="z-label text-muted-foreground">{label}</Label>
        {unitOptions && onUnitChange && (
          <UnitSwitcher
            options={unitOptions}
            value={unit}
            onChange={onUnitChange}
            disabled={disabled}
          />
        )}
      </div>
      <div
        className={cn(
          "flex h-9 items-center rounded-3xl border border-transparent bg-input/50 transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/30",
          isDirty &&
            !disabled &&
            "border-warning/60 bg-warning/10 focus-within:border-warning focus-within:ring-warning/30",
          disabled && "opacity-60"
        )}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) =>
            numericGuard(e.target.value) && onChange(e.target.value)
          }
          placeholder="0"
          disabled={disabled}
          className={cn(baseInput, "pl-4 pr-1")}
        />
        <span className="z-caption text-muted-foreground pr-3 select-none">
          {unit}
        </span>
        {compound && unit2 && onChange2 && (
          <>
            <div className="h-4 w-px bg-border/80" />
            <input
              type="text"
              inputMode="decimal"
              value={value2 || ""}
              onChange={(e) =>
                numericGuard(e.target.value) && onChange2(e.target.value)
              }
              placeholder="0"
              disabled={disabled}
              className={cn(baseInput, "pl-3 pr-1")}
            />
            <span className="z-caption text-muted-foreground pr-3 select-none">
              {unit2}
            </span>
          </>
        )}
      </div>
      {isDirty && !disabled && originalLabel && (
        <div className="z-hint text-muted-foreground pl-3 flex items-center gap-1.5">
          <span>Was {originalLabel}</span>
          {onRevert && (
            <button
              type="button"
              tabIndex={-1}
              onClick={onRevert}
              aria-label={`Revert to ${originalLabel}`}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Undo2 className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Shared dirty-state hint: "Was <value>" + undo button.
interface DirtyHintProps {
  show: boolean;
  originalLabel: string;
  onRevert: () => void;
}

const DirtyHint: React.FC<DirtyHintProps> = ({
  show,
  originalLabel,
  onRevert,
}) => {
  const visible = show && !!originalLabel;
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        "z-hint text-muted-foreground pl-1 flex items-center gap-1.5 h-4",
        !visible && "invisible"
      )}
    >
      <span>Was {originalLabel || "—"}</span>
      <button
        type="button"
        tabIndex={visible ? 0 : -1}
        onClick={onRevert}
        aria-label={`Revert to ${originalLabel}`}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        <Undo2 className="h-3 w-3" />
      </button>
    </div>
  );
};

// =============================================================================
// Body Measurements Card
// =============================================================================

type WeightUnit = "kgs" | "lbs";
type HeightUnit = "ft" | "cms";
type LengthUnit = "cms" | "in";

const cmToInch = (cm: number) => cm / 2.54;
const inchToCm = (inch: number) => inch * 2.54;
const kgToLb = (kg: number) => kg * 2.20462;
const lbToKg = (lb: number) => lb / 2.20462;

const BodyMeasurementsCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: healthProfile, isLoading } = useHealthProfile(appointmentId);
  const updateHealthProfile = useUpdateHealthProfile(appointmentId);
  const healthProfileSource = "meal_plan" as const;

  const [formData, setFormData] = useState({
    weight: "",
    height_ft: "",
    height_in: "",
    height_cms: "",
    waist: "",
    hip: "",
  });

  const [units, setUnits] = useState<{
    weight: WeightUnit;
    height: HeightUnit;
    waist: LengthUnit;
    hip: LengthUnit;
  }>({
    weight: "kgs",
    height: "ft",
    waist: "cms",
    hip: "cms",
  });

  // Build the display-string representation for a given healthProfile + unit set.
  // Used both for initial formData load and for the "baseline" (what the user
  // would see if they hadn't touched anything), so dirty detection is just
  // string equality.
  const toDisplay = React.useCallback(
    (
      hp: typeof healthProfile,
      u: {
        weight: WeightUnit;
        height: HeightUnit;
        waist: LengthUnit;
        hip: LengthUnit;
      }
    ) => {
      const out = {
        weight: "",
        height_ft: "",
        height_in: "",
        height_cms: "",
        waist: "",
        hip: "",
      };
      if (!hp) return out;

      if (hp.weight_in_kgs != null) {
        out.weight =
          u.weight === "lbs"
            ? fmt(kgToLb(hp.weight_in_kgs))
            : fmt(hp.weight_in_kgs);
      }

      if (hp.height_in_cms != null) {
        if (u.height === "ft") {
          const totalInches = hp.height_in_cms / 2.54;
          let ft = Math.floor(totalInches / 12);
          let inc = Math.round(totalInches - ft * 12);
          if (inc === 12) {
            ft += 1;
            inc = 0;
          }
          out.height_ft = String(ft);
          out.height_in = String(inc);
        } else {
          out.height_cms = fmt(hp.height_in_cms, 0);
        }
      }

      if (hp.waist_in_cms != null) {
        out.waist =
          u.waist === "in"
            ? fmt(cmToInch(hp.waist_in_cms))
            : fmt(hp.waist_in_cms);
      }

      if (hp.hip_in_cms != null) {
        out.hip =
          u.hip === "in" ? fmt(cmToInch(hp.hip_in_cms)) : fmt(hp.hip_in_cms);
      }

      return out;
    },
    []
  );

  // Load on healthProfile change only — do NOT reset when units change, since
  // the unit-switch handlers already convert the in-progress formData.
  useEffect(() => {
    if (healthProfile) {
      setFormData(toDisplay(healthProfile, units));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthProfile]);

  const switchWeightUnit = (next: string) => {
    const nextUnit = next as WeightUnit;
    if (nextUnit === units.weight) return;
    setFormData((prev) => {
      const n = parseFloat(prev.weight);
      if (!Number.isFinite(n)) return prev;
      return {
        ...prev,
        weight: nextUnit === "lbs" ? fmt(kgToLb(n)) : fmt(lbToKg(n)),
      };
    });
    setUnits((u) => ({ ...u, weight: nextUnit }));
  };

  const switchHeightUnit = (next: string) => {
    const nextUnit = next as HeightUnit;
    if (nextUnit === units.height) return;
    setFormData((prev) => {
      if (nextUnit === "cms") {
        const ft = parseFloat(prev.height_ft || "0");
        const inc = parseFloat(prev.height_in || "0");
        if (!ft && !inc) return prev;
        return { ...prev, height_cms: fmt((ft * 12 + inc) * 2.54, 0) };
      }
      const cm = parseFloat(prev.height_cms);
      if (!Number.isFinite(cm)) return prev;
      const totalInches = cm / 2.54;
      let ft = Math.floor(totalInches / 12);
      let inc = Math.round(totalInches - ft * 12);
      if (inc === 12) {
        ft += 1;
        inc = 0;
      }
      return {
        ...prev,
        height_ft: ft.toString(),
        height_in: inc.toString(),
      };
    });
    setUnits((u) => ({ ...u, height: nextUnit }));
  };

  const baseline = useMemo(
    () => toDisplay(healthProfile, units),
    [healthProfile, units, toDisplay]
  );

  const dirty = useMemo(
    () => ({
      weight: formData.weight !== baseline.weight,
      height:
        units.height === "ft"
          ? formData.height_ft !== baseline.height_ft ||
            formData.height_in !== baseline.height_in
          : formData.height_cms !== baseline.height_cms,
      waist: formData.waist !== baseline.waist,
      hip: formData.hip !== baseline.hip,
    }),
    [formData, baseline, units.height]
  );

  const anyDirty = dirty.weight || dirty.height || dirty.waist || dirty.hip;

  useDirtyFlag(
    `body-measurements:${appointmentId}`,
    anyDirty,
    "Body Measurements"
  );

  const weightOriginal = baseline.weight
    ? `${baseline.weight} ${units.weight}`
    : "";
  const heightOriginal =
    units.height === "ft"
      ? baseline.height_ft || baseline.height_in
        ? `${baseline.height_ft || "0"}'${baseline.height_in || "0"}"`
        : ""
      : baseline.height_cms
        ? `${baseline.height_cms} cms`
        : "";
  const waistOriginal = baseline.waist
    ? `${baseline.waist} ${units.waist}`
    : "";
  const hipOriginal = baseline.hip ? `${baseline.hip} ${units.hip}` : "";

  const revertWeight = () =>
    setFormData((p) => ({ ...p, weight: baseline.weight }));
  const revertHeight = () =>
    setFormData((p) => ({
      ...p,
      height_ft: baseline.height_ft,
      height_in: baseline.height_in,
      height_cms: baseline.height_cms,
    }));
  const revertWaist = () =>
    setFormData((p) => ({ ...p, waist: baseline.waist }));
  const revertHip = () => setFormData((p) => ({ ...p, hip: baseline.hip }));

  const switchLengthUnit = (field: "waist" | "hip") => (next: string) => {
    const nextUnit = next as LengthUnit;
    const currentUnit = units[field];
    if (nextUnit === currentUnit) return;
    setFormData((prev) => {
      // If the field is still at baseline, convert from the canonical cms
      // value so round-tripping units doesn't introduce rounding drift.
      const canonicalCm =
        field === "waist"
          ? healthProfile?.waist_in_cms
          : healthProfile?.hip_in_cms;
      if (canonicalCm != null) {
        const pristineDisplay =
          currentUnit === "in" ? fmt(cmToInch(canonicalCm)) : fmt(canonicalCm);
        if (prev[field] === pristineDisplay) {
          return {
            ...prev,
            [field]:
              nextUnit === "in" ? fmt(cmToInch(canonicalCm)) : fmt(canonicalCm),
          };
        }
      }
      const n = parseFloat(prev[field]);
      if (!Number.isFinite(n)) return prev;
      return {
        ...prev,
        [field]: nextUnit === "in" ? fmt(cmToInch(n)) : fmt(inchToCm(n)),
      };
    });
    setUnits((u) => ({ ...u, [field]: nextUnit }));
  };

  const handleSave = () => {
    const data: Parameters<typeof updateHealthProfile.mutate>[0] = {};

    if (formData.weight) {
      const n = parseFloat(formData.weight);
      if (Number.isFinite(n)) {
        data.weight_in_kgs = parseFloat(
          (units.weight === "lbs" ? lbToKg(n) : n).toFixed(2)
        );
      }
    }

    if (units.height === "ft") {
      const ft = parseInt(formData.height_ft || "0", 10);
      const inches = parseFloat(formData.height_in || "0");
      if (ft || inches) {
        data.height_in_cms = parseFloat(((ft * 12 + inches) * 2.54).toFixed(2));
      }
    } else if (formData.height_cms) {
      const n = parseFloat(formData.height_cms);
      if (Number.isFinite(n)) data.height_in_cms = parseFloat(n.toFixed(2));
    }

    if (formData.waist) {
      const n = parseFloat(formData.waist);
      if (Number.isFinite(n)) {
        data.waist_in_cms = parseFloat(
          (units.waist === "in" ? inchToCm(n) : n).toFixed(2)
        );
      }
    }

    if (formData.hip) {
      const n = parseFloat(formData.hip);
      if (Number.isFinite(n)) {
        data.hip_in_cms = parseFloat(
          (units.hip === "in" ? inchToCm(n) : n).toFixed(2)
        );
      }
    }

    if (healthProfile?.activity_level) {
      data.activity_level = healthProfile.activity_level;
    }
    data.source = healthProfileSource;

    updateHealthProfile.mutate(data);
  };

  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  return (
    <Widget>
      <WidgetHeader title="Body Measurements" />
      <WidgetContent className="space-y-3">
        <div className="flex flex-wrap gap-4">
          <MeasurementField
            className="w-40"
            label="Weight"
            unit={units.weight}
            unitOptions={["kgs", "lbs"] as const}
            onUnitChange={switchWeightUnit}
            value={formData.weight}
            onChange={(v) => setFormData((p) => ({ ...p, weight: v }))}
            disabled={isCompleted}
            isDirty={dirty.weight}
            originalLabel={weightOriginal}
            onRevert={revertWeight}
          />
          <MeasurementField
            className="w-52"
            label="Height"
            unit={units.height === "ft" ? "ft" : "cms"}
            unitOptions={["ft", "cms"] as const}
            onUnitChange={switchHeightUnit}
            compound={units.height === "ft"}
            unit2={units.height === "ft" ? "in" : undefined}
            value={
              units.height === "ft" ? formData.height_ft : formData.height_cms
            }
            onChange={(v) =>
              setFormData((p) =>
                units.height === "ft"
                  ? { ...p, height_ft: v }
                  : { ...p, height_cms: v }
              )
            }
            value2={units.height === "ft" ? formData.height_in : undefined}
            onChange2={(v) => setFormData((p) => ({ ...p, height_in: v }))}
            disabled={isCompleted}
            isDirty={dirty.height}
            originalLabel={heightOriginal}
            onRevert={revertHeight}
          />
          <MeasurementField
            className="w-40"
            label="Waist"
            unit={units.waist}
            unitOptions={["cms", "in"] as const}
            onUnitChange={switchLengthUnit("waist")}
            value={formData.waist}
            onChange={(v) => setFormData((p) => ({ ...p, waist: v }))}
            disabled={isCompleted}
            isDirty={dirty.waist}
            originalLabel={waistOriginal}
            onRevert={revertWaist}
          />
          <MeasurementField
            className="w-40"
            label="Hip"
            unit={units.hip}
            unitOptions={["cms", "in"] as const}
            onUnitChange={switchLengthUnit("hip")}
            value={formData.hip}
            onChange={(v) => setFormData((p) => ({ ...p, hip: v }))}
            disabled={isCompleted}
            isDirty={dirty.hip}
            originalLabel={hipOriginal}
            onRevert={revertHip}
          />
        </div>

        {!isCompleted && (
          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!anyDirty || updateHealthProfile.isPending}
            >
              {updateHealthProfile.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </div>
        )}
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// Feeding Method Card
// =============================================================================

const FeedingMethodCard: React.FC = () => {
  const {
    appointmentId,
    isCompleted,
    feedingTypes,
    currentFeedingType,
    isLoadingFeedingTypes,
  } = useNutritionEvaluationContext();

  const setFeedingType = useSetFeedingType(appointmentId);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFeedingTypeId, setPendingFeedingTypeId] = useState<
    number | null
  >(null);

  const handleFeedingTypeChange = (value: string) => {
    const newTypeId = parseInt(value);
    if (currentFeedingType && currentFeedingType.feeding_type !== newTypeId) {
      setPendingFeedingTypeId(newTypeId);
      setShowConfirmDialog(true);
    } else {
      setFeedingType.mutate({
        feedingTypeId: newTypeId,
        existingRecordId: currentFeedingType?.id,
      });
    }
  };

  const confirmFeedingTypeChange = () => {
    if (pendingFeedingTypeId !== null) {
      setFeedingType.mutate({
        feedingTypeId: pendingFeedingTypeId,
        existingRecordId: currentFeedingType?.id,
      });
    }
    setShowConfirmDialog(false);
    setPendingFeedingTypeId(null);
  };

  const hasSelection = Boolean(currentFeedingType?.feeding_type);

  if (isLoadingFeedingTypes) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  return (
    <>
      <Widget>
        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <div className="flex items-center gap-1">
            <span className="z-body-md text-foreground">Feeding Method</span>
            <span
              className="text-destructive z-body-md leading-none"
              aria-hidden
            >
              *
            </span>
            <span className="sr-only">(required)</span>
          </div>
          {feedingTypes.length === 0 ? (
            <EmptyState title="No feeding types available" variant="compact" />
          ) : (
            <Select
              value={currentFeedingType?.feeding_type?.toString() || ""}
              onValueChange={handleFeedingTypeChange}
              disabled={isCompleted || setFeedingType.isPending}
              required
            >
              <SelectTrigger
                aria-required="true"
                aria-invalid={!hasSelection}
                className={cn(
                  "w-56",
                  !hasSelection &&
                    !isCompleted &&
                    "border-destructive/60 ring-destructive/20"
                )}
              >
                <SelectValue placeholder="Select feeding method" />
              </SelectTrigger>
              <SelectContent>
                {feedingTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {setFeedingType.isPending && (
          <div className="flex items-center gap-2 z-body text-muted-foreground px-3 pb-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Updating feeding type...
          </div>
        )}
      </Widget>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="border-border/60">
          <AlertDialogHeader>
            <AlertDialogTitle className="z-h2 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Change Feeding Method?
            </AlertDialogTitle>
            <AlertDialogDescription>
              If you change the feeding method, all your current progress in
              Diet Recall and Meal Plan will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-foreground border-border/60">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmFeedingTypeChange}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Yes, Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// =============================================================================
// Energy Burned Card
// =============================================================================

const EnergyBurnedCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: healthProfile, isLoading } = useHealthProfile(appointmentId);
  const updateHealthProfile = useUpdateHealthProfile(appointmentId);
  const healthProfileSource = "meal_plan" as const;

  const [activityLevel, setActivityLevel] = useState<ActivityLevel>("");

  useEffect(() => {
    if (healthProfile?.activity_level) {
      setActivityLevel(healthProfile.activity_level);
    }
  }, [healthProfile]);

  const originalActivity = (healthProfile?.activity_level ??
    "") as ActivityLevel;
  const isDirty = activityLevel !== originalActivity;

  useDirtyFlag(`energy-burned:${appointmentId}`, isDirty, "Energy Burned");

  const activityLabel = (value: string) =>
    ACTIVITY_LEVEL_OPTIONS.find((o) => o.value === value)?.label ?? value;

  const handleRevert = () => setActivityLevel(originalActivity);

  const handleSave = () => {
    updateHealthProfile.mutate({
      activity_level: activityLevel,
      weight_in_kgs: healthProfile?.weight_in_kgs,
      height_in_cms: healthProfile?.height_in_cms,
      waist_in_cms: healthProfile?.waist_in_cms,
      hip_in_cms: healthProfile?.hip_in_cms,
      source: healthProfileSource,
    });
  };

  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  const bmrValue = healthProfile?.bmr != null ? fmt(healthProfile.bmr, 2) : "";
  const paValue =
    healthProfile?.pa_calories != null ? fmt(healthProfile.pa_calories, 2) : "";

  const headerExtras = (bmrValue || paValue) && (
    <div className="flex items-center gap-2">
      {bmrValue && (
        <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 z-label text-muted-foreground">
          BMR <span className="text-foreground font-medium">{bmrValue}</span>{" "}
          kcal
        </span>
      )}
      {paValue && (
        <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 z-label text-muted-foreground">
          PA Cal <span className="text-foreground font-medium">{paValue}</span>{" "}
          kcal
        </span>
      )}
    </div>
  );

  return (
    <Widget>
      <WidgetHeader title="Energy Burned" extras={headerExtras} />
      <WidgetContent>
        <div className="flex flex-wrap gap-4">
          <div className="flex w-52 flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label className="z-label text-muted-foreground">
                Physical Activity
              </Label>
            </div>
            <Select
              value={activityLevel}
              onValueChange={(v) => setActivityLevel(v as ActivityLevel)}
              disabled={isCompleted || updateHealthProfile.isPending}
            >
              <SelectTrigger
                className={cn(
                  "w-full",
                  isDirty &&
                    !isCompleted &&
                    "border-warning/60 bg-warning/10 focus-visible:border-warning focus-visible:ring-warning/30"
                )}
              >
                <SelectValue placeholder="Select activity level" />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_LEVEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isDirty && !isCompleted && originalActivity && (
              <div className="z-hint text-muted-foreground flex items-center gap-1.5 pl-3">
                <span>Was {activityLabel(originalActivity)}</span>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={handleRevert}
                  aria-label={`Revert to ${activityLabel(originalActivity)}`}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Undo2 className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>

          {!isCompleted && (
            <div className="ml-auto flex flex-col gap-1.5">
              <span aria-hidden className="z-label invisible select-none">
                _
              </span>
              <Button
                size="sm"
                className="h-9"
                onClick={handleSave}
                disabled={!isDirty || updateHealthProfile.isPending}
              >
                {updateHealthProfile.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </div>
          )}
        </div>
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// Symptoms Card
// =============================================================================

const SymptomsCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: appointmentSymptoms, isLoading: isLoadingAppointmentSymptoms } =
    useAppointmentNutritionSymptoms(appointmentId);
  const addSymptom = useAddNutritionSymptom(appointmentId);
  const removeSymptom = useRemoveNutritionSymptom(appointmentId);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    "Others" | "Digestive"
  >("Others");
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  const { data: searchResults, isLoading: isSearching } =
    useSearchNutritionSymptoms(
      searchQuery,
      appointmentId,
      selectedCategory === "Digestive" ? "digestive" : "others",
      showAddDialog
    );

  const handleDialogOpenChange = (open: boolean) => {
    setShowAddDialog(open);
    if (!open) setSearchQuery("");
  };

  const getSymptomDetails = (s: AppointmentNutritionSymptom) =>
    s.symptoms || s.symptom_details;

  // Symptom drafts use activity_status; inactive draft items must stay
  // hidden until prescription/meal-plan publish flushes them.
  const activeAppointmentSymptoms = useMemo(
    () =>
      appointmentSymptoms?.filter(
        (s) => (s.activity_status ?? "active") === "active"
      ) ?? [],
    [appointmentSymptoms]
  );

  const otherSymptoms = useMemo(
    () =>
      activeAppointmentSymptoms.filter((s) => {
        const details = getSymptomDetails(s);
        return (
          details?.type !== "digestive" && details?.category !== "Digestive"
        );
      }),
    [activeAppointmentSymptoms]
  );

  const digestiveSymptoms = useMemo(
    () =>
      activeAppointmentSymptoms.filter((s) => {
        const details = getSymptomDetails(s);
        return (
          details?.type === "digestive" || details?.category === "Digestive"
        );
      }),
    [activeAppointmentSymptoms]
  );

  const availableSymptoms = useMemo(() => {
    if (!searchResults) return [];
    const addedIds = new Set(
      activeAppointmentSymptoms.map((s) => {
        const details = getSymptomDetails(s);
        return s.symptom || details?.id;
      })
    );
    return searchResults.filter((s) => !addedIds.has(s.id));
  }, [searchResults, activeAppointmentSymptoms]);

  const handleAddSymptom = (symptom: NutritionSymptom) => {
    addSymptom.mutate({ symptomId: symptom.id });
  };

  const handleRemoveSymptom = (symptomId: string) => {
    setPendingRemoveId(symptomId);
  };

  const confirmRemoveSymptom = () => {
    if (pendingRemoveId) {
      removeSymptom.mutate(pendingRemoveId);
      setPendingRemoveId(null);
    }
  };

  const pendingRemoveName = useMemo(() => {
    if (!pendingRemoveId || !appointmentSymptoms) return "";
    const match = appointmentSymptoms.find((s) => s.id === pendingRemoveId);
    return match
      ? getSymptomDetails(match)?.name || "this symptom"
      : "this symptom";
  }, [pendingRemoveId, appointmentSymptoms]);

  if (isLoadingAppointmentSymptoms) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  return (
    <>
      <Widget>
        <WidgetHeader title="Active Symptoms" />
        <WidgetContent>
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <Table>
              <TableBody>
                {[
                  {
                    key: "Digestive" as const,
                    label: "Digestive",
                    rows: digestiveSymptoms,
                  },
                  {
                    key: "Others" as const,
                    label: "Other Symptoms",
                    rows: otherSymptoms,
                  },
                ].map(({ key, label, rows }) => (
                  <TableRow key={key} className="hover:bg-transparent">
                    <TableCell className="py-2 px-3 whitespace-nowrap w-0">
                      <span className="z-eyebrow text-muted-foreground">
                        {label}
                      </span>
                      {rows.length > 0 && (
                        <span className="z-hint text-muted-foreground/70 ml-1">
                          ({rows.length})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 px-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {rows.length > 0 ? (
                          rows.map((symptom) => (
                            <DismissibleChip
                              key={symptom.id}
                              label={getSymptomDetails(symptom)?.name || ""}
                              onRemove={
                                !isCompleted
                                  ? () => handleRemoveSymptom(symptom.id)
                                  : undefined
                              }
                            />
                          ))
                        ) : (
                          <span className="z-caption text-muted-foreground/50">
                            No data yet
                          </span>
                        )}
                        {!isCompleted && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCategory(key);
                              setShowAddDialog(true);
                            }}
                            className="flex items-center justify-center size-5 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                          >
                            <Plus className="size-3" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </WidgetContent>
      </Widget>

      {/* Add Symptoms Dialog */}
      <Dialog open={showAddDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
          <DialogHeader className="p-6 pb-4 shrink-0">
            <DialogTitle className="z-h2 text-foreground">
              Add Symptoms
            </DialogTitle>
          </DialogHeader>

          <div className="h-px bg-primary/30 shrink-0" />

          <div className="flex flex-col">
            {(otherSymptoms.length > 0 || digestiveSymptoms.length > 0) && (
              <div className="px-6 pt-4 pb-3 shrink-0 space-y-3">
                {digestiveSymptoms.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="z-eyebrow text-muted-foreground">Digestive</p>
                    <div className="flex flex-wrap gap-2">
                      {digestiveSymptoms.map((symptom) => (
                        <DismissibleChip
                          key={`added-dig-${symptom.id}`}
                          label={getSymptomDetails(symptom)?.name || ""}
                          onRemove={() => handleRemoveSymptom(symptom.id)}
                          className="border-primary/40 bg-primary/10 text-primary"
                          removeClassName="bg-primary/15 text-primary/60 hover:bg-destructive/20 hover:text-destructive"
                        />
                      ))}
                    </div>
                  </div>
                )}
                {otherSymptoms.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="z-eyebrow text-muted-foreground">
                      Other Symptoms
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {otherSymptoms.map((symptom) => (
                        <DismissibleChip
                          key={`added-other-${symptom.id}`}
                          label={getSymptomDetails(symptom)?.name || ""}
                          onRemove={() => handleRemoveSymptom(symptom.id)}
                          className="border-primary/40 bg-primary/10 text-primary"
                          removeClassName="bg-primary/15 text-primary/60 hover:bg-destructive/20 hover:text-destructive"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="px-6 pt-3 pb-4 space-y-3">
              <div className="flex gap-2">
                <Button
                  variant={
                    selectedCategory === "Others" ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => {
                    setSelectedCategory("Others");
                    setSearchQuery("");
                  }}
                >
                  Other Symptoms
                </Button>
                <Button
                  variant={
                    selectedCategory === "Digestive" ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => {
                    setSelectedCategory("Digestive");
                    setSearchQuery("");
                  }}
                >
                  Digestives
                </Button>
              </div>

              <p className="z-secondary text-muted-foreground">
                Add symptoms here
              </p>
              <Command
                shouldFilter={false}
                className="overflow-visible bg-transparent"
              >
                <div className="relative">
                  <CommandInput
                    placeholder="Search Here"
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    autoFocus
                    className="z-body"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                  )}
                </div>
              </Command>

              <div className="max-h-[260px] overflow-y-auto scrollbar-thin pt-1">
                {availableSymptoms.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {availableSymptoms.map((symptom) => (
                      <DismissibleChip
                        key={symptom.id}
                        label={symptom.name}
                        onAdd={() => handleAddSymptom(symptom)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4 text-muted-foreground z-secondary">
                    {searchQuery
                      ? "No matching symptoms found"
                      : "No symptoms available"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingRemoveId !== null}
        onOpenChange={(open) => !open && setPendingRemoveId(null)}
      >
        <AlertDialogContent className="border-border/60">
          <AlertDialogHeader>
            <AlertDialogTitle className="z-h2">
              Mark symptom as inactive?
            </AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{pendingRemoveName}&rdquo; will be marked inactive and
              removed from this patient&apos;s symptoms.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-foreground border-border/60">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveSymptom}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Mark Inactive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// =============================================================================
// Sleep Pattern Card
// =============================================================================

// Time picker that stores "HH:mm" (24h) and displays three themed selects
// for hour (1-12), minute (00-59) and period (AM/PM).
const HOURS_12 = Array.from({ length: 12 }, (_, i) =>
  (i + 1).toString().padStart(2, "0")
);
const MINUTES_60 = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0")
);

const to12h = (value: string) => {
  if (!value) return { hour: "", minute: "", period: "AM" as "AM" | "PM" };
  const [hStr, mStr] = value.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m))
    return { hour: "", minute: "", period: "AM" as "AM" | "PM" };
  const period: "AM" | "PM" = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;
  return {
    hour: hour12.toString().padStart(2, "0"),
    minute: m.toString().padStart(2, "0"),
    period,
  };
};

const to24h = (hour: string, minute: string, period: "AM" | "PM") => {
  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  let h24 = h % 12;
  if (period === "PM") h24 += 12;
  return `${h24.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  isDirty?: boolean;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  disabled,
  isDirty,
}) => {
  const { hour, minute, period } = to12h(value);

  const emit = (
    nextHour: string,
    nextMinute: string,
    nextPeriod: "AM" | "PM"
  ) => {
    if (!nextHour || !nextMinute) return;
    onChange(to24h(nextHour, nextMinute, nextPeriod));
  };

  const triggerClass =
    "h-9 w-14 px-2 gap-1 bg-muted/40 border-transparent hover:bg-muted/60 focus:bg-card data-[state=open]:bg-card justify-center z-numeric";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-muted/30 p-1 border border-transparent",
        isDirty && !disabled && "border-warning/60 bg-warning/10"
      )}
    >
      <Select
        value={hour}
        onValueChange={(v) => emit(v, minute || "00", period)}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass} aria-label="Hour">
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent className="max-h-64 min-w-[4.5rem]">
          {HOURS_12.map((h) => (
            <SelectItem key={h} value={h} className="justify-center z-numeric">
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="z-body text-muted-foreground select-none">:</span>
      <Select
        value={minute}
        onValueChange={(v) => emit(hour || "12", v, period)}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass} aria-label="Minute">
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent className="max-h-64 min-w-[4.5rem]">
          {MINUTES_60.map((m) => (
            <SelectItem key={m} value={m} className="justify-center z-numeric">
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={period}
        onValueChange={(v) =>
          emit(hour || "12", minute || "00", v as "AM" | "PM")
        }
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass} aria-label="Period">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="min-w-[4.5rem]">
          <SelectItem value="AM" className="justify-center">
            AM
          </SelectItem>
          <SelectItem value="PM" className="justify-center">
            PM
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

const SleepPatternCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: sleepData, isLoading } = useSleepData(appointmentId);
  const updateSleepData = useUpdateSleepData(appointmentId);

  const [formData, setFormData] = useState<Partial<SleepData>>({
    wake_up_time: "",
    sleep_time: "",
    has_sleep_issues: false,
    sleep_issues_note: "",
  });

  useEffect(() => {
    if (sleepData) {
      setFormData({
        wake_up_time: sleepData.wake_up_time || "",
        sleep_time: sleepData.sleep_time || "",
        has_sleep_issues: sleepData.has_sleep_issues || false,
        sleep_issues_note: sleepData.sleep_issues_note || "",
      });
    }
  }, [sleepData]);

  const sleepBaseline = useMemo(
    () => ({
      wake_up_time: sleepData?.wake_up_time || "",
      sleep_time: sleepData?.sleep_time || "",
      has_sleep_issues: sleepData?.has_sleep_issues || false,
      sleep_issues_note: sleepData?.sleep_issues_note || "",
    }),
    [sleepData]
  );

  const sleepDirty = useMemo(
    () => ({
      wake_up_time:
        (formData.wake_up_time || "") !== sleepBaseline.wake_up_time,
      sleep_time: (formData.sleep_time || "") !== sleepBaseline.sleep_time,
      has_sleep_issues:
        Boolean(formData.has_sleep_issues) !== sleepBaseline.has_sleep_issues,
      sleep_issues_note:
        (formData.sleep_issues_note || "") !== sleepBaseline.sleep_issues_note,
    }),
    [formData, sleepBaseline]
  );

  const sleepAnyDirty =
    sleepDirty.wake_up_time ||
    sleepDirty.sleep_time ||
    sleepDirty.has_sleep_issues ||
    sleepDirty.sleep_issues_note;

  useDirtyFlag(
    `sleep-pattern:${appointmentId}`,
    sleepAnyDirty,
    "Sleep Pattern"
  );

  const format12h = (value: string) => {
    const { hour, minute, period } = to12h(value);
    if (!hour || !minute) return "";
    return `${hour}:${minute} ${period}`;
  };

  const revertSleepField = (field: keyof typeof sleepBaseline) =>
    setFormData((prev) => ({ ...prev, [field]: sleepBaseline[field] }));

  const calculateSleepDuration = () => {
    if (formData.wake_up_time && formData.sleep_time) {
      const [wakeHour, wakeMin] = formData.wake_up_time.split(":").map(Number);
      const [sleepHour, sleepMin] = formData.sleep_time.split(":").map(Number);

      let wakeMinutes = wakeHour * 60 + wakeMin;
      const sleepMinutes = sleepHour * 60 + sleepMin;

      if (wakeMinutes <= sleepMinutes) {
        wakeMinutes += 24 * 60;
      }

      const durationMinutes = wakeMinutes - sleepMinutes;
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    }
    return null;
  };

  const getSleepQuality = () => {
    const duration = calculateSleepDuration();
    if (duration) {
      const [hours] = duration.split(":").map(Number);
      if (hours < 5) return { label: "Very Poor", color: "critical" as const };
      if (hours < 6) return { label: "Poor", color: "critical" as const };
      if (hours < 7) return { label: "Fair", color: "warning" as const };
      if (hours < 8) return { label: "Good", color: "success" as const };
      return { label: "Excellent", color: "success" as const };
    }
    return null;
  };

  const formatSleepDuration = (value: string | null) => {
    if (!value) return null;
    const [h, m] = value.split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (m === 0) return `${h} hr${h === 1 ? "" : "s"}`;
    return `${h}h ${m}m`;
  };

  const sleepDuration = calculateSleepDuration();
  const sleepQuality = getSleepQuality();

  const handleSave = () => {
    const isNew = !sleepData;
    updateSleepData.mutate({ data: formData as SleepData, isNew });
  };

  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  const durationLabel = formatSleepDuration(sleepDuration);

  return (
    <Widget>
      <div className="flex items-center justify-between gap-2 px-3 py-1.5">
        <span className="z-body-md text-foreground">Sleep Pattern</span>
        {(durationLabel || sleepQuality) && (
          <div className="flex items-center gap-1.5">
            {durationLabel && (
              <Badge variant="neutral">
                <span className="text-muted-foreground">Duration:</span>
                <span className="text-foreground">{durationLabel}</span>
              </Badge>
            )}
            {sleepQuality && (
              <Badge variant={sleepQuality.color}>
                <span className="opacity-70">Quality:</span>
                <span>{sleepQuality.label}</span>
              </Badge>
            )}
          </div>
        )}
      </div>
      <WidgetContent className="space-y-2 pb-2">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="z-label text-muted-foreground">
              Wake Up Time
            </Label>
            <TimePicker
              value={formData.wake_up_time || ""}
              onChange={(v) => setFormData({ ...formData, wake_up_time: v })}
              disabled={isCompleted}
              isDirty={sleepDirty.wake_up_time}
            />
            <DirtyHint
              show={sleepDirty.wake_up_time && !isCompleted}
              originalLabel={format12h(sleepBaseline.wake_up_time)}
              onRevert={() => revertSleepField("wake_up_time")}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="z-label text-muted-foreground">Sleep Time</Label>
            <TimePicker
              value={formData.sleep_time || ""}
              onChange={(v) => setFormData({ ...formData, sleep_time: v })}
              disabled={isCompleted}
              isDirty={sleepDirty.sleep_time}
            />
            <DirtyHint
              show={sleepDirty.sleep_time && !isCompleted}
              originalLabel={format12h(sleepBaseline.sleep_time)}
              onRevert={() => revertSleepField("sleep_time")}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <Checkbox
              id="sleep-issues"
              checked={formData.has_sleep_issues || false}
              onCheckedChange={(checked) =>
                setFormData({
                  ...formData,
                  has_sleep_issues: checked === true,
                })
              }
              disabled={isCompleted}
              className={cn(
                sleepDirty.has_sleep_issues &&
                  !isCompleted &&
                  "border-warning/60 data-[state=checked]:bg-warning data-[state=checked]:border-warning"
              )}
            />
            <Label
              htmlFor="sleep-issues"
              className="z-body text-foreground cursor-pointer"
            >
              Ever had severe sleep issues and took medications?
            </Label>
          </div>
          <DirtyHint
            show={sleepDirty.has_sleep_issues && !isCompleted}
            originalLabel={sleepBaseline.has_sleep_issues ? "Yes" : "No"}
            onRevert={() => revertSleepField("has_sleep_issues")}
          />
        </div>

        {formData.has_sleep_issues && (
          <div className="space-y-1.5">
            <Input
              placeholder="Add a note — not visible to the patient"
              value={formData.sleep_issues_note || ""}
              onChange={(e) =>
                setFormData({ ...formData, sleep_issues_note: e.target.value })
              }
              disabled={isCompleted}
              className={cn(
                sleepDirty.sleep_issues_note &&
                  !isCompleted &&
                  "border-warning/60 bg-warning/10 focus-visible:border-warning focus-visible:ring-warning/30"
              )}
            />
            <DirtyHint
              show={sleepDirty.sleep_issues_note && !isCompleted}
              originalLabel={sleepBaseline.sleep_issues_note || "(empty)"}
              onRevert={() => revertSleepField("sleep_issues_note")}
            />
          </div>
        )}

        {!isCompleted && (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={updateSleepData.isPending || !sleepAnyDirty}
            >
              {updateSleepData.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </div>
        )}
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// Poor Food Habits Card
// =============================================================================

const FREQUENCY_OPTIONS = [
  { value: "None", label: "None" },
  { value: "1/wk", label: "1/wk" },
  { value: "2-3/wk", label: "2-3/wk" },
  { value: "4-5/wk", label: "4-5/wk" },
  { value: "6-7/wk", label: "6-7/wk" },
];

const PoorFoodHabitsCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: foodFrequency, isLoading } = useFoodFrequency(appointmentId);
  const updateFoodFrequency = useUpdateFoodFrequency(appointmentId);

  const [formData, setFormData] = useState<FoodFrequency>({
    fast_food_frequency: "",
    sweets_frequency: "",
    packaged_food_frequency: "",
    bakery_items_frequency: "",
  });

  useEffect(() => {
    if (foodFrequency) {
      setFormData(foodFrequency);
    }
  }, [foodFrequency]);

  const foodBaseline = useMemo<FoodFrequency>(
    () => ({
      fast_food_frequency: foodFrequency?.fast_food_frequency || "",
      sweets_frequency: foodFrequency?.sweets_frequency || "",
      packaged_food_frequency: foodFrequency?.packaged_food_frequency || "",
      bakery_items_frequency: foodFrequency?.bakery_items_frequency || "",
    }),
    [foodFrequency]
  );

  const foodDirty = useMemo(
    () => ({
      fast_food_frequency:
        (formData.fast_food_frequency || "") !==
        foodBaseline.fast_food_frequency,
      bakery_items_frequency:
        (formData.bakery_items_frequency || "") !==
        foodBaseline.bakery_items_frequency,
      packaged_food_frequency:
        (formData.packaged_food_frequency || "") !==
        foodBaseline.packaged_food_frequency,
      sweets_frequency:
        (formData.sweets_frequency || "") !== foodBaseline.sweets_frequency,
    }),
    [formData, foodBaseline]
  );

  const foodAnyDirty =
    foodDirty.fast_food_frequency ||
    foodDirty.bakery_items_frequency ||
    foodDirty.packaged_food_frequency ||
    foodDirty.sweets_frequency;

  useDirtyFlag(`food-habits:${appointmentId}`, foodAnyDirty, "Food Habits");

  const revertFoodField = (field: keyof FoodFrequency) =>
    setFormData((prev) => ({ ...prev, [field]: foodBaseline[field] }));

  const foodLabel = (value: string) =>
    FREQUENCY_OPTIONS.find((o) => o.value === value)?.label ||
    value ||
    "(empty)";

  const handleSave = () => {
    const isNew = !foodFrequency;
    updateFoodFrequency.mutate({ data: formData, isNew });
  };

  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  return (
    <Widget>
      <WidgetHeader title="Food Habits" />
      <WidgetContent className="space-y-2 pb-2">
        <div className="grid grid-cols-4 gap-3">
          {(
            [
              { key: "fast_food_frequency", label: "Fast Food" },
              { key: "bakery_items_frequency", label: "Bakery Items" },
              { key: "packaged_food_frequency", label: "Packaged Food" },
              { key: "sweets_frequency", label: "Sweets" },
            ] as const
          ).map(({ key, label }) => (
            <div key={key} className="space-y-1.5">
              <Label className="z-label text-muted-foreground">{label}</Label>
              <Select
                value={formData[key] || ""}
                onValueChange={(v) => setFormData({ ...formData, [key]: v })}
                disabled={isCompleted}
              >
                <SelectTrigger
                  className={cn(
                    "w-full",
                    foodDirty[key] &&
                      !isCompleted &&
                      "border-warning/60 bg-warning/10"
                  )}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DirtyHint
                show={foodDirty[key] && !isCompleted}
                originalLabel={foodLabel(foodBaseline[key] || "")}
                onRevert={() => revertFoodField(key)}
              />
            </div>
          ))}
        </div>

        {!isCompleted && (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={updateFoodFrequency.isPending || !foodAnyDirty}
            >
              {updateFoodFrequency.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </div>
        )}
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// Dietary Preference Card
// =============================================================================

const DIETARY_TYPES = [
  { value: "vegetarian", label: "Vegetarian" },
  { value: "non_vegetarian", label: "Non-Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "eggetarian", label: "Eggetarian" },
];

const DietaryPreferenceCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: preference, isLoading } = useDietaryPreference(appointmentId);
  const updatePreference = useUpdateDietaryPreference(appointmentId);

  const [formData, setFormData] = useState<{
    dietary_type: string;
    regional_preferences: string[];
  }>({
    dietary_type: "",
    regional_preferences: [],
  });

  useEffect(() => {
    if (preference) {
      setFormData({
        dietary_type: preference.dietary_type || "",
        regional_preferences: preference.regional_preferences ?? [],
      });
    }
  }, [preference]);

  const preferencesBaseline = useMemo(
    () => ({
      dietary_type: preference?.dietary_type || "",
      regional_preferences: preference?.regional_preferences ?? [],
    }),
    [preference]
  );

  const sortedKey = (arr: string[]) => [...arr].sort().join("|");

  const preferencesDirty = useMemo(
    () => ({
      dietary: formData.dietary_type !== preferencesBaseline.dietary_type,
      regional:
        sortedKey(formData.regional_preferences) !==
        sortedKey(preferencesBaseline.regional_preferences),
    }),
    [formData, preferencesBaseline]
  );
  const preferencesAnyDirty =
    preferencesDirty.dietary || preferencesDirty.regional;

  useDirtyFlag(
    `preferences:${appointmentId}`,
    preferencesAnyDirty,
    "Nutrition Preferences"
  );

  const dietaryLabel = (value: string) =>
    DIETARY_TYPES.find((t) => t.value === value)?.label || value || "(empty)";

  const getRegionalLabel = (value: string) =>
    REGIONAL_PREFERENCE_OPTIONS.find((opt) => opt.value === value)?.label ||
    value;

  const formatRegionalSummary = (values: string[]) =>
    values.length === 0 ? "(empty)" : values.map(getRegionalLabel).join(", ");

  const revertDietary = () =>
    setFormData((prev) => ({
      ...prev,
      dietary_type: preferencesBaseline.dietary_type,
    }));
  const revertRegional = () =>
    setFormData((prev) => ({
      ...prev,
      regional_preferences: preferencesBaseline.regional_preferences,
    }));

  const handleSave = () => {
    const isNew = !preference;
    updatePreference.mutate({
      data: {
        dietary_type: (formData.dietary_type || "vegetarian") as
          | "vegetarian"
          | "non_vegetarian"
          | "vegan"
          | "eggetarian",
        regional_preferences: formData.regional_preferences,
      },
      isNew,
    });
  };

  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  return (
    <Widget>
      <WidgetHeader title="Preferences" />
      <WidgetContent className="space-y-2 pb-2">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="z-label text-muted-foreground">Dietary</span>
              <Select
                value={formData.dietary_type}
                onValueChange={(v) =>
                  setFormData({ ...formData, dietary_type: v })
                }
                disabled={isCompleted}
              >
                <SelectTrigger
                  className={cn(
                    "w-44",
                    preferencesDirty.dietary &&
                      !isCompleted &&
                      "border-warning/60 bg-warning/10"
                  )}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {DIETARY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DirtyHint
              show={preferencesDirty.dietary && !isCompleted}
              originalLabel={dietaryLabel(preferencesBaseline.dietary_type)}
              onRevert={revertDietary}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="z-label text-muted-foreground">Regional</span>
              <MultiSelect
                options={REGIONAL_PREFERENCE_OPTIONS}
                value={formData.regional_preferences}
                onValueChange={(next) =>
                  setFormData({ ...formData, regional_preferences: next })
                }
                disabled={isCompleted}
                placeholder="Select"
                itemNoun="regions"
                searchPlaceholder="Search regions"
                triggerClassName={cn(
                  "w-44",
                  preferencesDirty.regional &&
                    !isCompleted &&
                    "border-warning/60 bg-warning/10"
                )}
              />
            </div>
            <DirtyHint
              show={preferencesDirty.regional && !isCompleted}
              originalLabel={formatRegionalSummary(
                preferencesBaseline.regional_preferences
              )}
              onRevert={revertRegional}
            />
          </div>
        </div>

        {!isCompleted && (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={updatePreference.isPending || !preferencesAnyDirty}
            >
              {updatePreference.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </div>
        )}
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// Food Preference Card
// =============================================================================

interface FoodPreferenceCardProps {
  isLiked: boolean;
}

const FoodPreferenceCard: React.FC<FoodPreferenceCardProps> = ({ isLiked }) => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const likedFoodQuery = useLikedFood(appointmentId);
  const dislikedFoodQuery = useDislikedFood(appointmentId);
  const { data: foods, isLoading } = isLiked
    ? likedFoodQuery
    : dislikedFoodQuery;
  const addPreference = useAddFoodPreference(appointmentId);
  const removePreference = useRemoveFoodPreference(appointmentId);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { data: searchResults, isLoading: isSearching } = useFoodSearch(
    searchQuery,
    appointmentId,
    showAddDialog && searchQuery.length >= 2
  );

  const handleAddFood = (foodId: number) => {
    addPreference.mutate(
      { foodItemId: foodId, isLiked },
      { onSuccess: () => setSearchQuery("") }
    );
  };

  const handleRemoveFood = (preferenceId: string) => {
    removePreference.mutate({ preferenceId, isLiked });
  };

  const title = isLiked ? "Most Liked Food" : "Disliked Food";

  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  return (
    <>
      <Widget>
        <WidgetHeader
          title={title}
          onAdd={!isCompleted ? () => setShowAddDialog(true) : undefined}
        />
        <WidgetContent>
          {foods && foods.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {foods.map((food) => (
                <DismissibleChip
                  key={food.id}
                  label={food.food_item.full_name}
                  onRemove={
                    !isCompleted ? () => handleRemoveFood(food.id) : undefined
                  }
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title={`No ${isLiked ? "liked" : "disliked"} food items added`}
              variant="compact"
            />
          )}
        </WidgetContent>
      </Widget>

      {/* Add Food Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[600px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
          <DialogHeader className="p-6 pb-0 shrink-0">
            <DialogTitle className="z-h2 text-foreground">
              Add {title}
            </DialogTitle>
            <DialogDescription className="mt-1">
              Search for food items to add to the list
            </DialogDescription>
          </DialogHeader>
          <Separator className="mt-4" />

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-background">
            <div className="px-6 py-4 border-b border-border/60 shrink-0">
              <h3 className="z-label text-muted-foreground mb-3">
                Already Added
              </h3>
              <div className="rounded-xl border border-border/60 p-3 max-h-[120px] overflow-y-auto scrollbar-thin">
                {foods && foods.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {foods.map((food) => (
                      <DismissibleChip
                        key={`added-${food.id}`}
                        label={food.food_item.full_name}
                        onRemove={() => handleRemoveFood(food.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="z-hint text-muted-foreground italic text-center py-2">
                    No {isLiked ? "liked" : "disliked"} food added yet
                  </p>
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col min-h-0 bg-background">
              <div className="p-6 border-b border-border/60 bg-background z-10 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search food items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-muted/50 border-border/60 rounded-xl z-body text-foreground placeholder:text-muted-foreground"
                    autoFocus
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
                <div className="flex flex-wrap gap-2">
                  {searchQuery.length < 2 ? (
                    <div className="w-full text-center py-8 z-body text-muted-foreground">
                      Type at least 2 characters to search
                    </div>
                  ) : searchResults?.results?.length === 0 ? (
                    <div className="w-full text-center py-8 z-body text-muted-foreground">
                      No food items found
                    </div>
                  ) : (
                    searchResults?.results?.map((food) => (
                      <DismissibleChip
                        key={food.id}
                        label={food.full_name}
                        onAdd={() => handleAddFood(food.id)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const NutritionProfileTab: React.FC = () => {
  const { isTubeFeeding } = useNutritionEvaluationContext();

  return (
    <div className="space-y-6">
      <BodyMeasurementsCard />
      <FeedingMethodCard />
      <EnergyBurnedCard />
      <SymptomsCard />
      <DietaryPreferenceCard />
      <SleepPatternCard />

      {!isTubeFeeding && (
        <>
          <PoorFoodHabitsCard />
          <FoodPreferenceCard isLiked={true} />
          <FoodPreferenceCard isLiked={false} />
        </>
      )}
    </div>
  );
};
